import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import NewsCard from "../components/NewsCard";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CATEGORIES = [
  { slug: "", label: "All" },
  { slug: "bitcoin", label: "Bitcoin" },
  { slug: "ethereum", label: "Ethereum" },
  { slug: "altcoins", label: "Altcoins" },
  { slug: "market-analysis", label: "Market Analysis" },
  { slug: "defi", label: "DeFi" },
  { slug: "nfts", label: "NFTs" },
];

const PAGE_SIZE = 12;

export default function NewsList() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const activeCat = params.get("category") || "";
  const page = parseInt(params.get("page") || "1", 10);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const skip = (page - 1) * PAGE_SIZE;
        let url = `/news?limit=${PAGE_SIZE}&skip=${skip}`;
        if (q) url = `/news?q=${encodeURIComponent(q)}&limit=${PAGE_SIZE}&skip=${skip}`;
        else if (activeCat) url = `/news?category=${activeCat}&limit=${PAGE_SIZE}&skip=${skip}`;
        const { data } = await api.get(url);
        setItems(data);
      } finally { setLoading(false); }
    })();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [q, activeCat, page]);

  const setCategory = (slug) => {
    const next = new URLSearchParams();
    if (slug) next.set("category", slug);
    setParams(next);
  };

  const goToPage = (p) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next);
  };

  const hasPrev = page > 1;
  const hasNext = items.length === PAGE_SIZE;

  return (
    <div className="pt-10 pb-20 max-w-[1400px] mx-auto px-6 md:px-10">

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-2">
        <h1 className="font-display font-bold text-5xl md:text-6xl text-[var(--text-primary)] tracking-tighter">
          {q ? <>Results for <span className="text-yellow-400">"{q}"</span></> : "Latest News"}
        </h1>

        {!q && (
          <div className="flex flex-wrap gap-2 mb-1">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setCategory(cat.slug)}
                className={
                  "px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition " +
                  (activeCat === cat.slug
                    ? "bg-yellow-400 text-black border-yellow-400"
                    : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400")
                }
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <p className="text-[var(--text-secondary)] mb-10">All editorial coverage from MediaCrypto.</p>

      {loading ? (
        <div className="text-[var(--text-tertiary)]">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-[var(--text-tertiary)]">No articles found.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="news-grid">
            {items.map((a) => <NewsCard key={a.id} article={a} />)}
          </div>

          {(hasPrev || hasNext) && (
            <div className="flex items-center justify-center gap-3 mt-14">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={!hasPrev}
                className="flex items-center gap-1 px-4 py-2 border border-[var(--border-soft)] text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400 transition disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-3 h-3" /> Prev
              </button>
              <span className="font-mono text-xs text-[var(--text-tertiary)] px-2">
                Page {page}
              </span>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={!hasNext}
                className="flex items-center gap-1 px-4 py-2 border border-[var(--border-soft)] text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400 transition disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}